import React, { useState, useEffect } from "react";
import { getEnvConfig } from "helpers/environment-config";
import { getAuthorizedHtmlRequest } from "utils/api";
import { ReportView } from "model/Report";
import styled from "styled-components";
import ReportTable from "./ReportTable";
import Button from "@material-ui/core/Button";

const PageHeader = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 25px;
`;

const HeaderText = styled.p`
  font-size: 24px;
  color: #0e3f62;
`;

const HeaderActions = styled.div`
  display: flex;
  align-items: center;
`;

const Action = styled.div`
  margin-right: 20px;
`;

const StyledPrimaryButton = styled(Button)`
  && {
    min-width: 90px;
    background-color: #105587;
    color: #fff;

    &:hover {
      background-color: #3f769f;
    }
  }
`;

const StyledOutlinedButton = styled(Button)`
  && {
    min-width: 90px;
    background-color: transparent;
    border: 1px solid #1468a5;
    color: #1468a5;

    &:hover {
      background-color: #e7eff6;
    }
  }
`;

const DomoReport = () => {
  const [availableReports, setAvailableReports] = useState<ReportView[]>([]);
  const [enabledReportIds, setEnabledReportIds] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const getReportData = () => {
    setIsLoading(true);
    getEnvConfig("reactAppReportsApiUrl").then(reportsBffUrl => {
      const reportListUrl = `${reportsBffUrl}/standard-reports?includeHidden=true`;

      getAuthorizedHtmlRequest(reportListUrl).then(reportData => {
        const reports = reportData as ReportView[];
        setAvailableReports(reports);
        setEnabledReportIds(reports.map(report => report.visible ? report.reportId : null).filter(Boolean) as string[]);
        setIsLoading(false);
      });
    });
  };

  useEffect(() => {
    getReportData();
  }, []);

  const handleSwitch = (id: string) => {
    const index = enabledReportIds.findIndex(reportId => reportId === id);
    if (index > -1) {
      const reportIds = enabledReportIds.filter(rId => rId !== id);
      setEnabledReportIds(reportIds);
    } else {
      setEnabledReportIds([...enabledReportIds, id]);
    }
  };

  const cancelChanges = () => {
    setEnabledReportIds(availableReports.map(report => report.visible ? report.reportId : null).filter(Boolean) as string[]);
  };

  const getReportIdsQueryParam = (id: string) => `reportIds=${id}`;

  const saveReportVisibility = async (reportIds: string[], path: string) => {
    if (!reportIds.length) {
      return;
    }

    const reportsBffUrl = await getEnvConfig("reactAppReportsApiUrl");
    const reportListUrl = `${reportsBffUrl}/${path}?${reportIds.map(m => getReportIdsQueryParam(m)).join("&")}`;
    await getAuthorizedHtmlRequest(reportListUrl, "POST");
  };

  const saveChanges = async () => {
    const showReports = availableReports.filter(f => !f.visible && enabledReportIds.includes(f.reportId)).map(m => m.reportId);
    const hideReports = availableReports.filter(f => f.visible && !enabledReportIds.includes(f.reportId)).map(m => m.reportId);

    await saveReportVisibility(showReports, "standard-reports/show");
    await saveReportVisibility(hideReports, "standard-reports/hide");

    if (showReports.length || hideReports.length) {
      getReportData();
    }
  };

  return (
    <div>
      <PageHeader>
        <HeaderText>Domo Report Settings</HeaderText>
        <HeaderActions>
          <Action><StyledOutlinedButton onClick={cancelChanges} disabled={isLoading}>
            Cancel
          </StyledOutlinedButton></Action>
          <Action><StyledPrimaryButton onClick={saveChanges} disabled={isLoading}>
            Save
          </StyledPrimaryButton></Action>
        </HeaderActions>
      </PageHeader>
      <ReportTable availableReportList={availableReports} enabledReportIds={enabledReportIds} handleSwitch={handleSwitch} />
    </div>
  );
};

export default DomoReport;
